import React from 'react';
import { ApolloError } from '@apollo/client';
import { Box } from '@mui/material';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Pagination,
  IPaginationProps,
  LoadedContent,
} from '@occc-app/design-system';
import { KioskDefaultScreenFragment } from '@occc/graphql';
import { useIsUserPermittedTo, Feature } from '@occc/utils-access-control';

import { DefaultScreensListTableRow } from './default-screens-list-table-row.component';

type DefaultScreensListTableProps = {
  defaultScreens?: KioskDefaultScreenFragment[];
  loading: boolean;
  error?: ApolloError;
  paginationProps: IPaginationProps;
  onItemClick: (defaultScreenId: string) => void;
  onEdit: (defaultScreenId: string) => void;
  onDelete: (defaultScreenId: string) => void;
};

export const DefaultScreensListTable = ({
  defaultScreens,
  loading,
  error,
  paginationProps,
  onItemClick,
  onEdit,
  onDelete,
}: DefaultScreensListTableProps) => {
  const isUserPermittedToEditDefaultScreen = useIsUserPermittedTo(
    Feature.TOUCHPOINT_DEFAULT_SCREEN_EDIT,
  );
  const isUserPermittedToDeleteDefaultScreen = useIsUserPermittedTo(
    Feature.TOUCHPOINT_DEFAULT_SCREEN_DELETE,
  );

  const areActionsVisible =
    isUserPermittedToEditDefaultScreen || isUserPermittedToDeleteDefaultScreen;

  const renderData = (items: KioskDefaultScreenFragment[]) => {
    const rows = items.map((defaultScreen) => (
      <DefaultScreensListTableRow
        key={defaultScreen.id}
        defaultScreen={defaultScreen}
        actions={{
          onItemClick,
          onEdit,
          onDelete,
        }}
        permissions={{
          canEdit: isUserPermittedToEditDefaultScreen,
          canDelete: isUserPermittedToDeleteDefaultScreen,
        }}
      />
    ));

    return (
      <>
        <Box sx={{ overflowX: 'auto' }}>
          <Table sx={{ minWidth: { xs: 600, md: 'auto' }, tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: '35%' }}>Name</TableCell>
                <TableCell sx={{ width: '45%' }}>Description</TableCell>
                <TableCell align='right' sx={{ width: 80 }}>
                  Image
                </TableCell>
                {areActionsVisible && <TableCell align='right' sx={{ width: 56 }}></TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>{rows}</TableBody>
          </Table>
        </Box>
        <Pagination {...paginationProps} />
      </>
    );
  };

  return (
    <LoadedContent<KioskDefaultScreenFragment>
      error={error}
      loading={loading}
      items={defaultScreens}
      renderData={renderData}
    />
  );
};
